import { useRef } from "react";

import { API_DEFAULT } from "./settings";
import { API } from "./routes";
import { request, tokenExpired } from "./http";

// The portal's session: the refresh of the five-minute access token, shared
// by every call that meets it expired; the sign-out, on the back and in
// localStorage; and the message when the session is over. Split out of
// Visor.jsx on 2026-09-21.
//
// The functions are built once and read the current tokens and setters when
// they run, so they keep the same identity across renders. Until 2026-09-22
// they were rebuilt on every render, and a component that put one in an
// effect's dependencies ran that effect on every render (it looped the
// reports panel).
export function useSession(props) {
  const latest = useRef(props);
  latest.current = props;

  const session = useRef(null);
  if (session.current === null) {
    session.current = buildSession(() => latest.current);
  }
  return session.current;
}

function buildSession(current) {

  const logOut = async (access = current().accessToken) => {
    const { refreshToken, setAccessToken, setShowLoader, setShowAlert, setMessage, setSeverity } = current();
    setShowLoader(true);

    const answer = await request(
      `${API_DEFAULT}/${API.logout}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${access}` },
      body: JSON.stringify({
        'all': false,
        'refresh': refreshToken
      })
    });

    if (answer.ok) {
      setShowLoader(false);
      signOff();
    }
    else if (tokenExpired(answer)) {
      let newAccess = await getAccessTokenWithRefresh();
      if (newAccess) {
        setAccessToken(newAccess);
        localStorage.setItem('accessToken', newAccess);
        logOut(newAccess);
      }
    }
    else {
      // A refusal, a 500 with an HTML page or no answer: the session stays,
      // the loader goes off and the user is told.
      setShowLoader(false);
      setMessage("Ocurrio un error, intente de nuevo.");
      setSeverity("danger");
      setTimeout(() => { setShowAlert(true) }, 0);
    }
  }

  // Signs out on the back and empties the session here.
  const signOff = () => {
    const { setAccessToken, setRefreshToken, setAuth, setPaths, setShowLoader } = current();
    setAccessToken("");
    setRefreshToken("");
    setAuth(false);
    setPaths(["/inicio-sesion", "/visor-rpd"]);
    localStorage.removeItem("accessToken")
    localStorage.removeItem("refreshToken")
    // Where earlier builds stored the refreshed access token; nothing reads it.
    localStorage.removeItem("access")
    localStorage.removeItem("auth")
    localStorage.removeItem("customPaths")
    localStorage.removeItem("user")
    localStorage.removeItem("selectedMenuButton")
    setShowLoader(false);
  }

  // Every call that meets an expired access token asks for a new one. They
  // used to ask at the same time, five refreshes for one expiry in the
  // production logs; now they share the one in flight.
  const refreshInFlight = { current: null };

  const sessionExpired = () => {
    const { setShowLoader, setShowAlert, setMessage, setSeverity } = current();
    signOff();
    setShowLoader(false)
    setMessage("La sesión expiró, ingrese nuevamente")
    setSeverity("info")
    setTimeout(() => { setShowAlert(true) }, 0)
  }

  const requestAccessToken = async () => {
    const { refreshToken, setShowLoader, setShowAlert, setMessage, setSeverity } = current();
    if (!refreshToken) {
      sessionExpired();
      return undefined;
    }

    // A 401 means the refresh token is not valid any more and the session is
    // over. Anything else (a 500 from the back, a network error) is not the
    // user's session ending: it is tried again, and the session is kept.
    for (let attempt = 0; attempt < 2; attempt++) {
      let res;
      try {
        res = await fetch(
          `${API_DEFAULT}/${API.refreshToken}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            'refresh': refreshToken
          })
        });
      } catch (e) {
        res = undefined;
      }

      if (res && res.status === 401) {
        sessionExpired();
        return undefined;
      }

      if (res && res.ok) {
        const data = await res.json().catch(() => undefined);
        if (data && data['code'] === 'token_not_valid') {
          sessionExpired();
          return undefined;
        }
        if (data && data['access']) {
          return data['access'];
        }
      }

      if (attempt === 0) {
        await new Promise(resolve => setTimeout(resolve, 3000));
      }
    }

    setShowLoader(false)
    setMessage("No fue posible actualizar la sesión. Intente de nuevo en unos segundos.")
    setSeverity("danger")
    setTimeout(() => { setShowAlert(true) }, 0)
    return undefined;
  }

  const getAccessTokenWithRefresh = async () => {
    if (!refreshInFlight.current) {
      refreshInFlight.current = requestAccessToken().finally(() => {
        refreshInFlight.current = null;
      });
    }
    return refreshInFlight.current;
  }

  return { getAccessTokenWithRefresh, logOut, signOff };
}
